import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { RefreshCw, Check, Shield, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import api from '../../services/api';
import ReadinessTrialPanel from './ReadinessTrialPanel';

const WatchdogTab = () => {
  const [events, setEvents] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [unresolvedOnly, setUnresolvedOnly] = useState('true');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);

  const fetchEvents = useCallback(async () => {
    setIsLoading(true);
    setErrorMsg(null);
    try {
      const { data } = await api.get(`/watchdog/events?unresolved_only=${unresolvedOnly}`);
      const eventsArray = Array.isArray(data) ? data : (Array.isArray(data?.events) ? data.events : []);
      setEvents(eventsArray);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setErrorMsg('Unauthorized. Please log in again as an admin.');
      } else {
        toast.error('Failed to fetch events');
      }
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  }, [unresolvedOnly]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const filteredEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    return events.filter(e => {
      const matchesSeverity = severityFilter === 'all' || e.severity === severityFilter;
      const matchesSearch = e.message.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            e.trading_account_id.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSeverity && matchesSearch;
    });
  }, [events, severityFilter, searchTerm]);

  const stats = useMemo(() => {
    if (!Array.isArray(events)) return {};
    return events.reduce((acc, e) => {
      if (e.resolved_at) return acc;
      acc[e.severity] = (acc[e.severity] || 0) + 1;
      return acc;
    }, {});
  }, [events]);

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const bulkResolve = async () => {
    try {
      await api.post('/watchdog/events/bulk-resolve', { event_ids: Array.from(selectedIds) });
      toast.success('Selected events resolved');
      setSelectedIds(new Set());
      fetchEvents();
    } catch (err) { toast.error('Bulk resolve failed'); }
  };

  const runCycle = async () => {
    try {
      await api.post('/watchdog/run-once');
      toast.success('Run cycle initiated');
      fetchEvents();
    } catch (err) { toast.error('Run cycle failed'); }
  };

  return (
    <div className="space-y-6">
      <ReadinessTrialPanel />
      {errorMsg ? (
        <div className="p-4 bg-red-100 text-red-700 rounded-md font-medium text-center">{errorMsg}</div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-4">
            <Card><CardHeader className="py-3"><CardTitle className="text-sm">Emergencies</CardTitle></CardHeader><CardContent className="text-2xl font-bold text-red-500">{stats.emergency || 0}</CardContent></Card>
            <Card><CardHeader className="py-3"><CardTitle className="text-sm">Action Required</CardTitle></CardHeader><CardContent className="text-2xl font-bold text-orange-500">{stats.action_required || 0}</CardContent></Card>
            <Card><CardHeader className="py-3"><CardTitle className="text-sm">Warnings</CardTitle></CardHeader><CardContent className="text-2xl font-bold text-yellow-500">{stats.warning || 0}</CardContent></Card>
            <Card><CardHeader className="py-3"><CardTitle className="text-sm">Info</CardTitle></CardHeader><CardContent className="text-2xl font-bold text-blue-500">{stats.info || 0}</CardContent></Card>
          </div>

          <div className="flex gap-2">
            <Select value={unresolvedOnly} onValueChange={setUnresolvedOnly}><SelectTrigger className="w-[150px]"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="true">Unresolved</SelectItem><SelectItem value="false">All</SelectItem></SelectContent></Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}><SelectTrigger className="w-[150px]"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="all">All Severities</SelectItem><SelectItem value="emergency">Emergency</SelectItem><SelectItem value="action_required">Action Required</SelectItem><SelectItem value="warning">Warning</SelectItem></SelectContent></Select>
            <Input placeholder="Search messages..." className="w-[300px]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            <Button onClick={runCycle}><RefreshCw className="w-4 h-4 mr-2" /> Run Cycle</Button>
            {selectedIds.size > 0 && <Button variant="destructive" onClick={bulkResolve}>Resolve ({selectedIds.size})</Button>}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"><Checkbox onCheckedChange={(checked) => setSelectedIds(checked ? new Set(filteredEvents.map(e => e.id)) : new Set())}/></TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Seen Count</TableHead>
                <TableHead>Detected</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEvents.map(e => (
                <TableRow key={e.id}>
                  <TableCell><Checkbox checked={selectedIds.has(e.id)} onCheckedChange={() => toggleSelect(e.id)} /></TableCell>
                  <TableCell><Badge variant={e.severity === 'emergency' ? 'destructive' : 'default'}>{e.severity}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">{e.trading_account_id}</TableCell>
                  <TableCell>{e.message}</TableCell>
                  <TableCell>{e.metadata_json.seen_count || 1}</TableCell>
                  <TableCell>{new Date(e.detected_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
};

export default WatchdogTab;
